import { from, Observable, of, switchMap } from 'rxjs';

import { MedicalCaseApiService } from '../../domain/medical-case/service/medical-case-api.service';

export class FileUtil {
  static getExtension(fileName: string): string | null {
    const dot = fileName.lastIndexOf('.');
    if (dot < 0) return null;
    return fileName.slice(dot + 1).toLowerCase();
  }

  static splitFileName(fileName: string): { baseName: string; extension: string | null } {
    const dot = fileName.lastIndexOf('.');
    if (dot <= 0) return { baseName: fileName, extension: null };
    return { baseName: fileName.slice(0, dot), extension: fileName.slice(dot + 1).toLowerCase() };
  }

  static ensureFileNameHasExtension(
    fileName: string | null | undefined,
    mimeType: string | null | undefined,
    fallbackBaseName = 'file'
  ): string {
    const normalizedName = fileName?.trim() || fallbackBaseName;
    if (FileUtil.getExtension(normalizedName)) {
      return normalizedName;
    }

    const inferredExtension = FileUtil.getExtensionForMimeType(mimeType);
    return inferredExtension
      ? `${normalizedName}.${inferredExtension}`
      : normalizedName;
  }

  static formatFileSize(bytes: number, decimals = 2): string {
    if (!bytes) return '0 B';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));
    return `${size} ${sizes[i]}`;
  }

  static truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}\n\n...`;
  }

  static csvToTable(
    text: string,
    maxRows: number,
    maxCols: number
  ): string[][] {
    const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
    const parsed = lines.map(l => FileUtil.parseCsvLine(l));
    return FileUtil.limitTable(parsed, maxRows, maxCols);
  }

  static limitTable(
    rows: unknown[][],
    maxRows: number,
    maxCols: number
  ): string[][] {
    return rows
      .slice(0, maxRows)
      .map(r => r.slice(0, maxCols).map(v => (v == null ? '' : String(v))));
  }

  static tryInferNameFromUrl(url: string | null): string | null {
    if (!url) return null;
    try {
      const u = new URL(url, window.location.origin);
      const last = u.pathname.split('/').filter(Boolean).at(-1);
      return last ? decodeURIComponent(last) : null;
    } catch {
      const last = url.split('/').filter(Boolean).at(-1);
      return last ?? null;
    }
  }

  static getFileTypeIcon(extension: string | null): {
    icon: string;
    colorClass: string;
    buttonClass: string;
  } {
    const ext = extension?.toLowerCase() ?? '';
    if (
      ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'svg'].includes(ext)
    ) {
      return {
        icon: 'bootstrapFileEarmarkImage',
        colorClass: 'text-purple',
        buttonClass: 'btn-outline-purple',
      };
    }
    if (['pdf'].includes(ext)) {
      return {
        icon: 'bootstrapFileEarmarkPdf',
        colorClass: 'text-danger',
        buttonClass: 'btn-outline-danger',
      };
    }
    if (['doc', 'docx', 'rtf'].includes(ext)) {
      return {
        icon: 'bootstrapFileEarmarkWord',
        colorClass: 'text-blue',
        buttonClass: 'btn-outline-blue',
      };
    }
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return {
        icon: 'bootstrapFileEarmarkSpreadsheet',
        colorClass: 'text-success',
        buttonClass: 'btn-outline-success',
      };
    }
    if (ext === 'txt') {
      return {
        icon: 'bootstrapFileEarmarkText',
        colorClass: 'text-secondary',
        buttonClass: 'btn-outline-secondary',
      };
    }
    if (ext === 'md') {
      return {
        icon: 'bootstrapFileEarmarkText',
        colorClass: 'text-dark-grey',
        buttonClass: 'btn-outline-secondary',
      };
    }
    return {
      icon: 'bootstrapFileEarmark',
      colorClass: 'text-muted',
      buttonClass: 'btn-outline-secondary',
    };
  }

  static loadFileContentAsText$(
    api: MedicalCaseApiService,
    caseId: string,
    fileId?: string
  ): Observable<string> {
    if (fileId == null) {
      return of('');
    }

    return api
      .getFileContent(caseId, fileId)
      .pipe(switchMap(content => from(content.text())));
  }

  private static parseCsvLine(line: string): string[] {
    const out: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
          continue;
        }
        inQuotes = !inQuotes;
        continue;
      }
      if (ch === ',' && !inQuotes) {
        out.push(current);
        current = '';
        continue;
      }
      current += ch;
    }
    out.push(current);
    return out;
  }

  private static getExtensionForMimeType(
    mimeType: string | null | undefined
  ): string | null {
    const normalized = mimeType?.split(';')[0]?.trim().toLowerCase() ?? '';
    switch (normalized) {
      case 'application/pdf':
        return 'pdf';
      case 'image/jpeg':
        return 'jpg';
      case 'image/png':
        return 'png';
      case 'image/gif':
        return 'gif';
      case 'image/bmp':
        return 'bmp';
      case 'image/webp':
        return 'webp';
      case 'image/tiff':
        return 'tiff';
      case 'image/svg+xml':
        return 'svg';
      case 'text/plain':
        return 'txt';
      case 'text/csv':
        return 'csv';
      case 'application/msword':
        return 'doc';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'docx';
      case 'application/vnd.ms-excel':
        return 'xls';
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return 'xlsx';
      case 'text/markdown':
      case 'text/markdown;charset=utf-8':
        return 'md';
      case 'application/json':
      case 'application/json;charset=utf-8':
        return 'json';
      default:
        return null;
    }
  }
}
